import type { Address } from 'viem';
import { create } from 'zustand';

import type { AuthenticatedOwnerSource } from '@/features/auth/authenticatedOwner';

/** The account the login belongs to, and the one the wallet now points at. */
export interface WalletSwitchPair {
    currentOwner: Address;
    nextAddress: Address;
    /**
     * Where `currentOwner` was read from. `oauth` means the social projection
     * was the only thing naming an owner, which is the weakest of the three:
     * that cohort funds deposits from whatever wallet is connected, so the
     * prompt must not offer signing out as its default answer.
     */
    ownerSource: AuthenticatedOwnerSource;
}

/**
 * Identity of one switch prompt: the two accounts and nothing else, so the same
 * question keeps its identity however the owner happened to be resolved.
 * Case-insensitive, so a checksummed and a lowercase spelling of the same switch
 * count as the same question.
 */
export function pairKey(
    pair: Pick<WalletSwitchPair, 'currentOwner' | 'nextAddress'>,
): string {
    return `${pair.currentOwner.toLowerCase()}:${pair.nextAddress.toLowerCase()}`;
}

interface WalletSwitchState {
    /** The switch currently being asked about, or null when nothing is asked. */
    pending: WalletSwitchPair | null;
    /**
     * The pair the user chose to keep ignoring. In memory only: a reload may
     * ask again, which is acceptable (and better than persisting a decision the
     * user has since forgotten making).
     */
    dismissedKey: string | null;
    request: (pair: WalletSwitchPair) => void;
    /** "Keep the current account": remembers the pair so it stops asking. */
    dismiss: () => void;
    /**
     * Closes the prompt without recording a decision, for when the question
     * stopped being meaningful (the wallet switched back, the user
     * disconnected, a flow that legitimately asks for another EOA started).
     */
    clear: () => void;
}

export const useWalletSwitchStore = create<WalletSwitchState>((set, get) => ({
    pending: null,
    dismissedKey: null,
    request: (pair) => set({ pending: pair }),
    dismiss: () => {
        const { pending } = get();
        set(
            pending
                ? { pending: null, dismissedKey: pairKey(pending) }
                : { pending: null },
        );
    },
    clear: () => set({ pending: null }),
}));
